/*
 * lotto/view.js — one HTML card renderer for the three places a card shows:
 * the print preview, the host's «check card» result and a player's phone.
 * The PDF draws the same grid from the same engine data (pdf.js), so the
 * paper and the screen cannot disagree about where a number sits.
 */
import { BINGO, lines } from './engine.js';

const STAR = '<svg viewBox="0 0 24 24" aria-hidden="true" class="lt-star"><path d="M12 2.6l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.4l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z"/></svg>';

function el(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
}

/*
 * opts:
 *   title, sub         header text (left, right)
 *   marked   Set       numbers the player daubed (phone)
 *   drawn    Set       numbers the host has drawn (check view)
 *   last     number    the latest barrel, ringed
 *   highlight Set      numbers of a completed pattern, emphasised
 *   near     Set       numbers that are the last one missing in a line
 *   called   Set       called by the host but not daubed (live room, manual daubing)
 *   interactive bool   cells are buttons; onToggle(n) is called on tap
 *   freeLabel string   accessible name of the FREE square
 */
export function renderCard(c, opts = {}) {
    const isUs = c.game === 'us75';
    const root = el('div', 'lt-card ' + (isUs ? 'lt-card-us' : 'lt-card-ticket'));
    root.dataset.game = c.game;
    if (opts.title || opts.sub) {
        const head = el('div', 'lt-card-h');
        head.appendChild(el('span', 'lt-card-t', opts.title || ''));
        if (opts.sub) head.appendChild(el('span', 'lt-card-s', opts.sub));
        root.appendChild(head);
    }

    const grid = el('div', 'lt-grid');
    grid.setAttribute('role', 'grid');
    grid.style.setProperty('--cols', String(c.grid[0].length));
    if (isUs) {
        for (let k = 0; k < 5; k++) {
            const h = el('div', 'lt-letter lt-l' + k, BINGO[k]);
            h.setAttribute('aria-hidden', 'true');
            grid.appendChild(h);
        }
    }
    const marked = opts.marked || new Set();
    const drawn = opts.drawn || new Set();
    const hi = opts.highlight || new Set();
    const near = opts.near || new Set();
    const called = opts.called || new Set();
    for (let r = 0; r < c.grid.length; r++) {
        for (let k = 0; k < c.grid[r].length; k++) {
            const n = c.grid[r][k];
            const free = isUs && r === 2 && k === 2;
            let cell;
            if (!n && !free) {
                cell = el('div', 'lt-cell lt-blank');
                cell.setAttribute('aria-hidden', 'true');
            } else if (free) {
                cell = el('div', 'lt-cell lt-free');
                cell.innerHTML = STAR;
                cell.setAttribute('role', 'gridcell');
                cell.setAttribute('aria-label', opts.freeLabel || 'FREE');
            } else {
                // no-tap-floor: the site-wide 44-px floor for buttons on touch
                // screens would make number cells taller than blank ones and
                // tear the grid; the player's card sets its own tap height.
                cell = el(opts.interactive ? 'button' : 'div', 'lt-cell lt-num no-tap-floor', String(n));
                cell.dataset.n = String(n);
                if (opts.interactive) {
                    cell.type = 'button';
                    cell.setAttribute('aria-pressed', marked.has(n) ? 'true' : 'false');
                } else {
                    cell.setAttribute('role', 'gridcell');
                }
                if (marked.has(n)) cell.classList.add('is-marked');
                if (drawn.has(n)) cell.classList.add('is-drawn');
                if (opts.last === n) cell.classList.add('is-last');
                if (hi.has(n)) cell.classList.add('is-win');
                if (near.has(n)) cell.classList.add('is-near');
                if (called.has(n)) cell.classList.add('is-called');
            }
            grid.appendChild(cell);
        }
    }
    if (opts.interactive && typeof opts.onToggle === 'function') {
        grid.addEventListener('click', (e) => {
            const b = e.target.closest('button.lt-num');
            if (b && grid.contains(b)) opts.onToggle(+b.dataset.n, b);
        });
    }
    root.appendChild(grid);
    return root;
}

/* Numbers of every line that is complete under `have` — used to light the
 * winning rows on a checked card and on the phone. */
export function completeLineNumbers(c, have) {
    const out = new Set();
    for (const l of lines(c)) if (l.nums.every((x) => have.has(x))) l.nums.forEach((x) => out.add(x));
    return out;
}
